import React, { useMemo, useEffect, useRef } from 'react';
import Plotly from 'plotly.js-dist-min';
import { 
    flattenDataForPlotly, 
    prepareLegendData, 
    SUPER_GENRE_COLOR_MAP 
} from '../utils/genreUtils';

const GenreSunburstModal = ({ show, onHide, data }) => {
    const plotRef = useRef(null);
    
    // 1. Prepare Chart Data
    const chartData = useMemo(() => {
        if (!data?.taxonomy) return null;
        
        const plotlyData = flattenDataForPlotly(
            data.taxonomy, 
            '', 
            '#adb5bd', 
            '', 
            SUPER_GENRE_COLOR_MAP
        );
        
        const finalColors = plotlyData.ids.map(id => plotlyData.colorMap[id]);

        return [{
            type: "sunburst",
            ids: plotlyData.ids,
            labels: plotlyData.labels,
            parents: plotlyData.parents,
            values: plotlyData.values,
            branchvalues: "total",
            hovertemplate: 
                '<span style="font-size: 15px; font-weight: 600;">%{customdata.label}</span><br>' +
                '<span style="font-size: 12px; color: #888;">%{customdata.path}</span>' +
                '<br><br>' +
                '<span style="font-size: 13px; color: #666;">Coverage:</span> <span style="font-size: 13px; font-weight: 600; color: #0066cc;">%{customdata.coverage}%</span><br>' +
                '<span style="font-size: 13px; color: #666;">Alignment:</span> <span style="font-size: 13px; font-weight: 600; color: #0066cc;">%{customdata.percentage}%</span>' +
                '<br><br>' +
                '<span style="font-size: 14px; line-height: 1.5; display: inline-block; max-width: 300px;">%{customdata.explanation}</span>' +
                '<extra></extra>',
            customdata: plotlyData.cleanLabels.map((label, i) => ({
                label: plotlyData.parents[i] === '' ? ' ' : label,
                path: plotlyData.parents[i] === '' ? ' ' : (plotlyData.paths[i] || ''),
                explanation: plotlyData.parents[i] === '' ? ' ' : (plotlyData.explanations[i] || ''),
                percentage: plotlyData.parents[i] === '' ? 0 : (plotlyData.percentages[i] || 0),
                coverage: plotlyData.parents[i] === '' ? 0 : (plotlyData.coverages[i] || 0)
            })),
            textinfo: "label",
            insidetextorientation: 'horizontal',
            textfont: {
                family: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                size: plotlyData.fontSizes,
                color: plotlyData.fontColors,
                weight: plotlyData.fontWeights
            },
            leaf: { opacity: 1 },
            marker: {
                colors: finalColors,
                line: { width: 2, color: 'white' }
            }
        }];
    }, [data]);

    // 2. Prepare Legend Data
    const legendData = useMemo(() => {
        if (!data?.taxonomy) return [];
        return prepareLegendData(data.taxonomy, SUPER_GENRE_COLOR_MAP);
    }, [data]);

    const nativeGenres = legendData.filter(item => !item.isBorrowedContext);
    const borrowedGenres = legendData.filter(item => item.isBorrowedContext);

    // 3. Render Plotly chart when data changes
    useEffect(() => {
        if (show && chartData && plotRef.current) {
            Plotly.newPlot(
                plotRef.current,
                chartData,
                {
                    margin: { l: 0, r: 0, b: 0, t: 0 },
                    autosize: true,
                    hoverlabel: {
                        bgcolor: 'white',
                        bordercolor: '#000',
                        font: { size: 13, color: '#333' },
                        align: 'left'
                    }
                },
                { displayModeBar: false, responsive: true }
            );
        }
        
        // Cleanup on unmount
        return () => {
            if (plotRef.current) {
                Plotly.purge(plotRef.current);
            }
        };
    }, [show, chartData]);

    if (!show) return null;

    return (
        <div 
            className="modal fade show" 
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={onHide}
            role="dialog"
            aria-labelledby="genre-modal-title"
            aria-modal="true"
        >
            <div 
                className="modal-dialog modal-xl modal-dialog-centered"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-content">
                    <div 
                        className="modal-header" 
                        style={{ backgroundColor: '#28b1a4', color: 'white' }}
                    >
                        <h5 className="modal-title" id="genre-modal-title">
                            Genre Analysis: {data?.title || 'Loading...'}
                        </h5>
                        <button 
                            type="button" 
                            className="btn-close btn-close-white" 
                            onClick={onHide}
                            aria-label="Close"
                        ></button>
                    </div>
                    <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                        {!data ? (
                            <div className="text-center p-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <div className="container-fluid">
                                <div className="row">
                                    {/* Chart Column */}
                                    <div className="col-lg-9 col-md-8 col-12 mb-4 mb-md-0" style={{ height: '600px' }}>
                                        <div 
                                            ref={plotRef}
                                            style={{ width: '100%', height: '100%' }}
                                        />
                                    </div>

                                    {/* Legend Column */}
                                    <div className="col-lg-3 col-md-4 col-12">
                                        <h3 className="h5 mb-3">Genre Alignment</h3>
                                        
                                        {nativeGenres.length > 0 && (
                                            <>
                                                <h4 className="text-muted fw-bold mt-2" style={{ fontSize: '0.85em' }}>Native Genres</h4>
                                                <ul className="list-unstyled">
                                                    {nativeGenres.map(item => (
                                                        <LegendItem key={item.name} item={item} />
                                                    ))}
                                                </ul>
                                            </>
                                        )}

                                        {borrowedGenres.length > 0 && (
                                            <>
                                                <h4 className="text-muted fw-bold mt-4" style={{ fontSize: '0.85em' }}>Contextual (Borrowed)</h4>
                                                <ul className="list-unstyled">
                                                    {borrowedGenres.map(item => (
                                                        <LegendItem key={item.name} item={item} isBorrowed />
                                                    ))}
                                                </ul>
                                            </>
                                        )}

                                        <div className="p-3 mt-4 bg-light rounded" style={{ borderLeft: '3px solid #0EA5E9', fontSize: '0.85em' }}>
                                            <strong>How to Read:</strong>
                                            <ul className="mb-0 ps-3 mt-1">
                                                <li><strong>Inner ring:</strong> Super-genres</li>
                                                <li><strong>Middle ring:</strong> Macro-genres</li>
                                                <li><strong>Outer ring:</strong> Micro-genres</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Helper Component for Legend List Items
const LegendItem = ({ item, isBorrowed }) => (
    <li className="mb-2">
        <div className="d-flex align-items-center" style={{ fontSize: '0.9em' }}>
            <span 
                style={{
                    width: '16px', height: '16px', marginRight: '8px',
                    borderRadius: '4px', border: '1px solid rgba(0,0,0,0.2)',
                    backgroundColor: item.color,
                    opacity: isBorrowed ? 0.5 : 1
                }} 
            />
            <span className="flex-grow-1">{item.name}</span>
            {!isBorrowed && item.percentage > 0 && (
                <span className="fw-bold ms-2">{item.percentage}%</span>
            )}
        </div>
        {item.borrowedChildren && item.borrowedChildren.length > 0 && (
            <div className="ps-4 fst-italic text-muted small">
                Borrowed: {item.borrowedChildren.join(', ')}
            </div>
        )}
    </li>
);

export default GenreSunburstModal;
